"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PORT_MAX = exports.PORT_MIN = exports.DNS_RESOLVERS = exports.DnsProvider = exports.VpnType = void 0;
exports.getVpnConfigTypes = getVpnConfigTypes;
exports.checkValidPort = checkValidPort;
exports.checkValidDns = checkValidDns;
exports.checkValidInstanceType = checkValidInstanceType;
exports.checkValidConfig = checkValidConfig;
const utils_1 = require("../utils");
const errors_1 = require("../errors");
const location_1 = require("./location");
const loglevel_1 = require("loglevel");
var VpnType;
(function (VpnType) {
    VpnType["WireGuard"] = "wireguard";
})(VpnType || (exports.VpnType = VpnType = {}));
var DnsProvider;
(function (DnsProvider) {
    DnsProvider["Cloudflare"] = "cloudflare";
    DnsProvider["Google"] = "google";
    DnsProvider["Quad9"] = "quad9";
    DnsProvider["OpenDNS"] = "opendns";
})(DnsProvider || (exports.DnsProvider = DnsProvider = {}));
exports.DNS_RESOLVERS = {
    [DnsProvider.Cloudflare]: { label: "Cloudflare (1.1.1.1)", ipv4: "1.1.1.1", ipv6: "2606:4700:4700::1111" },
    [DnsProvider.Google]: { label: "Google (8.8.8.8)", ipv4: "8.8.8.8", ipv6: "2001:4860:4860::8888" },
    [DnsProvider.Quad9]: { label: "Quad9 (9.9.9.9)", ipv4: "9.9.9.9", ipv6: "2620:fe::fe" },
    [DnsProvider.OpenDNS]: { label: "OpenDNS (208.67.222.222)", ipv4: "208.67.222.222", ipv6: "2620:119:35::35" },
};
exports.PORT_MIN = 0; // If 0, a random port will be assigned by the backend
exports.PORT_MAX = 65535;
function getVpnConfigTypes() {
    return Object.values(VpnType).filter((item) => {
        return isNaN(Number(item));
    });
}
function validateMaxPeanuts(peanuts, currentBalance, vpnConfigLimits) {
    // No matter what, if currentBalance is negative, we fail
    if (currentBalance <= 0) {
        throw new errors_1.NegativeBalanceError(currentBalance);
    }
    if (peanuts <= 0) {
        // result = Math.min(currentBalance, vpnConfigLimits.maxPeanutsFieldMaxValue)
        // This is always safe as it means run as long as there are enough peanuts
        return -1;
    }
    if (peanuts > currentBalance) {
        throw new errors_1.NotEnoughPeanutsError(peanuts, currentBalance);
    }
    if (peanuts < vpnConfigLimits.maxPeanutsFieldMinValue) {
        throw new errors_1.MinPeanutsError(vpnConfigLimits.maxPeanutsFieldMinValue, peanuts, currentBalance);
    }
    if (peanuts > vpnConfigLimits.maxPeanutsFieldMaxValue) {
        throw new errors_1.MaxPeanutsError(vpnConfigLimits.maxPeanutsFieldMaxValue, peanuts, currentBalance);
    }
    return peanuts;
}
function validateDeleteAfter(deleteAfter, vpnConfigLimits) {
    if (deleteAfter === undefined) {
        return vpnConfigLimits.deleteAfterFieldMaxValue;
    }
    if (deleteAfter < vpnConfigLimits.deleteAfterFieldMinValue) {
        throw new errors_1.MinDeleteAfterError(vpnConfigLimits.deleteAfterFieldMinValue, deleteAfter);
    }
    if (deleteAfter > vpnConfigLimits.deleteAfterFieldMaxValue) {
        throw new errors_1.MaxDeleteAfterError(vpnConfigLimits.deleteAfterFieldMaxValue, deleteAfter);
    }
    return deleteAfter;
}
function randomPort() {
    return Math.floor(Math.random() * 65535);
}
function checkValidPort(port) {
    if (port === undefined || port === null) {
        return randomPort();
    }
    const parsed = Number(port);
    if (!Number.isInteger(parsed)) {
        throw new errors_1.InvalidVpnConfigError(`Config port is invalid: ${port}`);
    }
    if (parsed < exports.PORT_MIN || parsed > exports.PORT_MAX) {
        throw new errors_1.InvalidVpnConfigError(`Config port must be between ${exports.PORT_MIN} and ${exports.PORT_MAX}, you specified: ${port}`);
    }
    if (port === 0)
        return randomPort();
    return parsed;
}
function randomDns() {
    return (0, utils_1.choose)(Object.values(DnsProvider));
}
function checkValidDns(dns) {
    if (dns === undefined || dns === null) {
        return randomDns();
    }
    const validValues = Object.values(DnsProvider);
    if (!validValues.includes(dns)) {
        throw new errors_1.InvalidVpnConfigError(`Config dns is invalid: ${dns}. Valid options: ${validValues.join(", ")}`);
    }
    return dns;
}
function checkValidInstanceType(instanceType) {
    if (instanceType === undefined || instanceType === null) {
        return location_1.InstanceType.Ultra;
    }
    const validValues = Object.values(location_1.InstanceType);
    if (!validValues.includes(instanceType)) {
        throw new errors_1.InvalidVpnConfigError(`Config instanceType is invalid: ${instanceType}. Valid options: ${validValues.join(", ")}`);
    }
    return instanceType;
}
// vpnConfig is of type any here because it could come from user input such as json
function checkValidConfig(userId, currentBalance, vpnConfigLimits, vpnConfig) {
    const maxPeanuts = checkValidMaxPeanuts(userId, currentBalance, vpnConfig.maxPeanuts, vpnConfigLimits);
    const type = checkValidVpnType(vpnConfig.type);
    const deleteAfter = checkValidDeleteAfter(vpnConfig.deleteAfter, vpnConfigLimits);
    const port = checkValidPort(vpnConfig.port);
    const dns = checkValidDns(vpnConfig.dns);
    const instanceType = checkValidInstanceType(vpnConfig.instanceType);
    const result = {
        maxPeanuts,
        type,
        deleteAfter,
        port,
        dns,
        ...(instanceType !== undefined && { instanceType }),
    };
    return result;
}
function checkValidMaxPeanuts(userId, currentBalance, maxPeanuts, vpnConfigLimits) {
    const peanuts = maxPeanuts ? Number.parseFloat(maxPeanuts) : -1;
    if (Number.isNaN(peanuts) || !Number.isFinite(peanuts)) {
        throw new errors_1.InvalidVpnConfigError(`Config maxPeanuts is invalid: ${maxPeanuts}`);
    }
    loglevel_1.default.debug(`Validating peanuts for ${userId}, peanuts: ${peanuts}, currentBalance: ${currentBalance}`);
    return validateMaxPeanuts(peanuts, currentBalance, vpnConfigLimits);
}
function checkValidVpnType(jsonType) {
    if (!jsonType) {
        throw new errors_1.InvalidVpnConfigError(`Config type is null or undefined: ${jsonType}`);
    }
    const type = jsonType;
    if (!type || !Object.values(VpnType).includes(type)) {
        throw new errors_1.InvalidVpnConfigError(`Config type is invalid: ${jsonType}`);
    }
    return type;
}
function checkValidDeleteAfter(jsonDeleteAfter, vpnConfigLimits) {
    if (jsonDeleteAfter === undefined) {
        return validateDeleteAfter(undefined, vpnConfigLimits);
    }
    const deleteAfter = Number.parseInt(jsonDeleteAfter);
    if (Number.isNaN(deleteAfter)) {
        throw new errors_1.InvalidVpnConfigError(`Config deleteAfter is invalid: ${jsonDeleteAfter}`);
    }
    return validateDeleteAfter(deleteAfter, vpnConfigLimits);
}
